import { ReactNode, useState } from 'react';
import { PlusOutlined } from '@ant-design/icons';
import { Access } from '@prisma/client';
import { Button, ButtonProps, Modal, Tabs, TabsProps } from 'antd';

import { SourceAccessItem } from '@app/domain-access-interfaces';
import { Hooks, Modals, Tables } from '@app/web-components';

import { AgsSearchPanel, BundleSearchPanel, IemSearchPanel } from './panels';

type AccessSelectModalProps = {
  agsOnly?: boolean;
  children?: ReactNode;
  disabled?: Tables.SelectTableDisabledType<SourceAccessItem | Access>;
  allowNonRequestableRoles?: boolean;
  mode?: 'single' | 'multiple';
  type?: ButtonProps['type'];
  value?: (SourceAccessItem | Access)[];
  onChange?: (newValue: (SourceAccessItem | Access)[]) => void;
};

export const AccessSelectModal = ({
  agsOnly,
  children = 'Add',
  disabled,
  mode = 'multiple',
  type,
  allowNonRequestableRoles,
  ...props
}: AccessSelectModalProps) => {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState<(SourceAccessItem | Access)[]>(props.value ?? []);
  const { value: searchText, getInputProps } = Hooks.useDebouncedInputValue();
  const modalProps = Modals.useDraggable({ children: 'Find Access' });

  const handleOnClick = () => {
    setOpen(true);
    setValue(props.value ?? []);
  };

  const handleOnCancel = () => {
    setOpen(false);
    setValue(props.value ?? []);
  };

  const handleOnOk = () => {
    setOpen(false);
    props.onChange && props.onChange(value);
  };

  const tabs: TabsProps['items'] = [
    {
      key: 'ags',
      label: 'Find AGS Entitlements and Roles',
      children: (
        <AgsSearchPanel
          allowNonRequestableRoles={allowNonRequestableRoles}
          disabled={disabled}
          mode={mode}
          value={value}
          onChange={setValue}
          searchText={searchText}
          searchInputProps={getInputProps()}
        />
      ),
    },
  ];

  if (!agsOnly) {
    tabs.push({
      key: 'iem',
      label: 'Find IEM Groups',
      children: (
        <IemSearchPanel
          disabled={disabled}
          mode={mode}
          value={value}
          onChange={setValue}
          searchText={searchText}
          searchInputProps={getInputProps()}
        />
      ),
    });

    tabs.push({
      key: 'bundle',
      label: 'Copy From Existing Bundle',
      children: <BundleSearchPanel disabled={disabled} mode={mode} value={value} onChange={setValue} />,
    });
  }

  return (
    <>
      <Modal
        styles={{
          body: {
            height: 600,
          },
        }}
        centered
        okText="Save"
        open={open}
        width={1100}
        onCancel={handleOnCancel}
        onOk={handleOnOk}
        {...modalProps}
      >
        <Tabs defaultActiveKey={tabs?.[0].key} items={tabs} />
      </Modal>
      <Button icon={<PlusOutlined />} type={type} onClick={handleOnClick}>
        {children}
      </Button>
    </>
  );
};
